import PropTypes from 'prop-types';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
// material
import { alpha, styled } from '@mui/material/styles';
import {
  Box,
  Stack,
  Drawer,
  Tooltip,
  Typography,
  CardActionArea,
  TextField,
  InputAdornment,
  IconButton
} from '@mui/material';
import { Search as SearchIcon, ChevronLeft, ChevronRight } from '@mui/icons-material';
import useCollapseDrawer from 'hooks/useCollapseDrawer';
// routes
// components
import MyAvatar from 'components/MyAvatar';
import Scrollbar from 'components/Scrollbar';
import NavSection from 'components/NavSection';
import { MHidden } from 'components/@material-extend';
//
import buildSidebarConfig from 'routes/SidebarConfig';
import LogoNavbar from 'components/LogoNavbar';
import useAuth from 'hooks/useAuth';

// ----------------------------------------------------------------------

const DRAWER_WIDTH = 240;
const COLLAPSE_WIDTH = 70;

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    transition: theme.transitions.create('width', {
      duration: theme.transitions.duration.complex
    })
  }
}));

// ----------------------------------------------------------------------

IconCollapse.propTypes = {
  onToggleCollapse: PropTypes.func,
  collapseClick: PropTypes.bool
};

function IconCollapse({ onToggleCollapse, collapseClick }) {
  return (
    <Tooltip title="Mini Menu">
      <CardActionArea
        onClick={onToggleCollapse}
        sx={{
          width: 18,
          height: 18,
          display: 'flex',
          cursor: 'pointer',
          borderRadius: '50%',
          alignItems: 'center',
          color: 'text.primary',
          justifyContent: 'center',
          border: 'solid 1px currentColor',
          ...(collapseClick && {
            borderWidth: 2
          })
        }}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: 'currentColor',
            transition: (theme) => theme.transitions.create('all'),
            ...(collapseClick && {
              width: 0,
              height: 0
            })
          }}
        />
      </CardActionArea>
    </Tooltip>
  );
}

DashboardSidebar.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func,
  sidebarConfig: PropTypes.array,
  onShortcutDragStart: PropTypes.func,
};

export default function DashboardSidebar({ isOpenSidebar, onCloseSidebar, handlePermitSound, sidebarConfig: sidebarConfigProp = null, onShortcutDragStart = null }) {
  const { pathname } = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const { areaPermissions = [] } = useAuth();

  const { isCollapse, collapseClick, collapseHover, onToggleCollapse, onHoverEnter, onHoverLeave } =
    useCollapseDrawer();

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const normalizeSearchText = useCallback(
    (value = '') =>
      value
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim(),
    []
  );

  // Función para buscar en el menú recursivamente
  const searchInMenu = useCallback((items, term) => {
    if (!term || !items) return items;

    const normalizedTerm = normalizeSearchText(term);
    const terms = normalizedTerm.split(/\s+/).filter(Boolean);

    const filteredItems = items.map(item => {
      const normalizedTitle = normalizeSearchText(item.title);
      const matchesSearch = terms.every((currentTerm) => normalizedTitle.includes(currentTerm));

      if (item.children && item.children.length > 0) {
        const filteredChildren = searchInMenu(item.children, term);
        const hasMatchingChildren = filteredChildren.length > 0;

        if (matchesSearch || hasMatchingChildren) {
          return {
            ...item,
            children: filteredChildren,
            isExpanded: true // Expandir automáticamente si hay resultados
          };
        }
        return null;
      }

      return matchesSearch ? item : null;
    }).filter(Boolean);

    return filteredItems;
  }, [normalizeSearchText]);

  const areaAccess = useMemo(() => {
    const hasScoped = Array.isArray(areaPermissions) && areaPermissions.length > 0;
    if (!hasScoped) {
      return { calendar: true, kanban: true, postIt: true, quotes: true };
    }
    return {
      calendar: areaPermissions.some((item) => item.calendar_access && item.calendar_access !== 'none'),
      kanban: areaPermissions.some((item) => item.kanban_access && item.kanban_access !== 'none'),
      postIt: areaPermissions.some((item) => item.post_it_access && item.post_it_access !== 'none'),
      quotes: areaPermissions.some((item) => item.quotes_access && item.quotes_access !== 'none')
    };
  }, [areaPermissions]);

  const sidebarConfig = useMemo(
    () =>
      sidebarConfigProp || buildSidebarConfig({
        showAreaCalendar: areaAccess.calendar,
        showAreaKanban: areaAccess.kanban,
        showAreaPostIt: areaAccess.postIt,
        showQuotes: areaAccess.quotes
      }),
    [areaAccess, sidebarConfigProp]
  );

  // Filtrar la configuración del sidebar basado en la búsqueda
  const filteredSidebarConfig = useMemo(() => {
    if (!searchTerm) return sidebarConfig;

    const filtered = sidebarConfig.map(section => ({
      ...section,
      items: searchInMenu(section.items, searchTerm)
    })).filter((section) => section.items.length > 0);

    return filtered;
  }, [searchTerm, searchInMenu, sidebarConfig]);

  // Verificar si hay resultados de búsqueda
  const hasSearchResults = useMemo(() => {
    if (!searchTerm) return true;
    return filteredSidebarConfig.some(section => section.items.length > 0);
  }, [filteredSidebarConfig, searchTerm]);

  const renderContent = (
    <Scrollbar
      onClick={handlePermitSound}
      sx={{
        height: 1,
        '& .simplebar-content': {
          height: 1,
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <Stack
        sx={{
          px: 1.5,
          pt: 1,
          pb: 1,
          ...(isCollapse && {
            alignItems: 'center'
          })
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="center">
          <Box
            component={RouterLink}
            to="/"
            sx={{
              display: 'inline-flex',
              textDecoration: 'none',
              color: 'inherit'
            }}>
            <LogoNavbar />
          </Box>

        </Stack>

        {isCollapse ? (
          <MyAvatar sx={{ mx: 'auto', mb: 2 }} />
        ) : (
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Buscar en el menú..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
                sx: {
                  backgroundColor: 'background.paper',
                  borderRadius: 1,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'divider'
                  }
                }
              }}
            />
          </Box>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Tooltip title={isCollapse ? 'Expandir menú' : 'Colapsar menú'}>
            <IconButton
              size="small"
              onClick={onToggleCollapse}
              sx={{
                border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                bgcolor: 'background.paper',
                boxShadow: (theme) => theme.customShadows.z8
              }}
            >
              {isCollapse ? <ChevronRight fontSize="small" /> : <ChevronLeft fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
      </Stack>

      <NavSection navConfig={filteredSidebarConfig} isShow={!isCollapse} searchTerm={searchTerm} onShortcutDragStart={onShortcutDragStart} />

      {searchTerm && !hasSearchResults && !isCollapse && (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No se encontraron resultados para "{searchTerm}"
          </Typography>
        </Box>
      )}

      <Box sx={{ flexGrow: 1 }} />
    </Scrollbar>
  );

  return (
    <RootStyle
      sx={{
        width: {
          lg: isCollapse ? COLLAPSE_WIDTH : DRAWER_WIDTH
        },
        ...(collapseClick && {
          position: 'absolute'
        })
      }}
    >
      <MHidden width="lgUp">
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          PaperProps={{
            sx: {
              width: DRAWER_WIDTH,
              bgcolor: (theme) => theme.palette.background.default,
              borderRight: (theme) => `1px solid ${alpha(theme.palette.divider, 0.6)}`,
              backgroundImage: (theme) =>
                `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(
                  theme.palette.background.default,
                  0.98
                )} 35%)`
            }
          }}
        >
          {renderContent}
        </Drawer>
      </MHidden>

      <MHidden width="lgDown">
        <Drawer
          open
          variant="persistent"
          onMouseEnter={onHoverEnter}
          onMouseLeave={onHoverLeave}
          PaperProps={{
            sx: {
              width: DRAWER_WIDTH,
              bgcolor: (theme) => theme.palette.background.default,
              borderRight: (theme) => `1px solid ${alpha(theme.palette.divider, 0.6)}`,
              backgroundImage: (theme) =>
                `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(
                  theme.palette.background.default,
                  0.98
                )} 35%)`,
              ...(isCollapse && {
                width: COLLAPSE_WIDTH
              }),
              ...(collapseHover && {
                borderRight: 0,
                backdropFilter: 'blur(6px)',
                WebkitBackdropFilter: 'blur(6px)', // Fix on Mobile
                boxShadow: (theme) => theme.customShadows.z20,
                bgcolor: (theme) => alpha(theme.palette.background.default, 0.92)
              })
            }
          }}
        >
          {renderContent}
        </Drawer>
      </MHidden>
    </RootStyle>
  );
}
